import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Maximize2, X } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, PieChart, Pie, Cell, ComposedChart } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import ChartModal from './ChartModal';
import { useFilters } from '../contexts/FilterContext';
import { maintenanceAnalyticsAPI } from '../services/api';

// Helper to format dates for API in local time
const formatDateForApi = (date) => {
  if (!(date instanceof Date)) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Maintenance = () => {
  const { filters, manualTrigger, setAvailableMachines } = useFilters();

  // State for drill-down functionality
  const [selectedEquipmentForDrillDown, setSelectedEquipmentForDrillDown] = useState(null);
  const [selectedEquipmentIdForDrillDown, setSelectedEquipmentIdForDrillDown] = useState(null);
  const [selectedServiceTypeForDrillDown, setSelectedServiceTypeForDrillDown] = useState(null);
  const prevSelectedEquipmentIdRef = useRef(null);
  const prevSelectedEquipmentRef = useRef(null);
  const prevSelectedServiceTypeRef = useRef(null);

  // State management for maximize functionality
  const [maximizedChartId, setMaximizedChartId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState({}); // Track sort order for each chart
  const [modalEquipmentFilter, setModalEquipmentFilter] = useState('all'); // Track equipment/service filter for modal
  const [modalSortOrder, setModalSortOrder] = useState('low-high'); // Track sort order for modal

  // Track if Maintenance component has loaded its initial data (LOCAL state, not global)
  const [maintenanceInitialDataLoaded, setMaintenanceInitialDataLoaded] = useState(false);

  // Track previous manualTrigger to detect when arrow button is clicked
  const prevManualTriggerRef = useRef(manualTrigger);

  // Track if API call is in progress to prevent duplicate calls
  const isFetchingRef = useRef(false);

  // Track if initial call has been made to prevent duplicate initial calls
  const initialCallMadeRef = useRef(false);

  // State for maintenance data from API
  const [maintenanceData, setMaintenanceData] = useState([]);

  // State for chart data - initialized with empty arrays/objects
  const [keyMetrics, setKeyMetrics] = useState({
    repairs: { value: 0, unit: 'Repairs' },
    sumOfRepair: { value: 0, unit: 'Hours' },
    mtbf: { value: 0, unit: 'Hours' },
    mdt: { value: 0, unit: 'Hours' }
  });
  const [sumOfRepairHoursByEquipment, setSumOfRepairHoursByEquipment] = useState([]);
  const [sumOfRepairHoursByServiceType, setSumOfRepairHoursByServiceType] = useState([]);
  const [mtbfByEquipmentNo, setMtbfByEquipmentNo] = useState([]);
  const [mdtByEquipmentNo, setMdtByEquipmentNo] = useState([]);
  const [mdtByServiceType, setMdtByServiceType] = useState([]);
  const [mtbfByServiceType, setMtbfByServiceType] = useState([]);
  const [sumOfUptimeByDate, setSumOfUptimeByDate] = useState([]);
  const [mdtByDate, setMdtByDate] = useState([]);
  const [mtbfByDate, setMtbfByDate] = useState([]);
  const [sumOfUptimeResolution, setSumOfUptimeResolution] = useState('day');
  const [mtbfResolution, setMtbfResolution] = useState('day');
  const [uptimeDataMap, setUptimeDataMap] = useState({ day: [], month: [], quarter: [], year: [] });
  const [mtbfDataMap, setMtbfDataMap] = useState({ day: [], month: [], quarter: [], year: [] });

  // Colors for Chart 1 bars
  const barColors = ['#ec4899', '#a78bfa', '#7c3aed', '#06b6d4', '#10b981', '#eab308', '#f97316', '#7c3aed'];

  // Fetch maintenance analytics data from API
  const fetchMaintenanceData = useCallback(async () => {
    // Prevent duplicate calls
    if (isFetchingRef.current) {
      console.log('⏸️ API call already in progress, skipping duplicate request');
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      console.log('🔄 Fetching maintenance analytics data...');

      // Get date range from filters or use default (this month start to today)
      const today = new Date();
      const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);

      const startDate = filters.dateRange?.from
        ? formatDateForApi(filters.dateRange.from)
        : formatDateForApi(thisMonthStart);
      const endDate = filters.dateRange?.to
        ? formatDateForApi(filters.dateRange.to)
        : formatDateForApi(today);

      // Build API filters - only include selected filters
      const apiFilters = {};

      // Add company ID if selected (required)
      if (filters.selectedCompany) {
        apiFilters.companyId = filters.selectedCompany;
      }

      // Add date range if both dates are selected
      if (startDate && endDate) {
        apiFilters.fromDate = startDate;
        apiFilters.toDate = endDate;
      }

      // Add optional filters ONLY if they are selected (not empty/null/undefined)
      if (filters.selectedLocation) {
        apiFilters.locationId = filters.selectedLocation;
        console.log('📍 Location filter:', filters.selectedLocation);
      }
      if (filters.selectedShift) {
        apiFilters.shiftNo = filters.selectedShift;
        console.log('⏰ Shift filter:', filters.selectedShift);
      }

      // Add equipmentId for drill-down functionality
      if (selectedEquipmentIdForDrillDown) {
        apiFilters.equipmentId = selectedEquipmentIdForDrillDown;
        console.log('🔍 Equipment drill-down:', selectedEquipmentIdForDrillDown);
      } else if (filters.selectedMachine) {
        apiFilters.equipmentId = filters.selectedMachine;
        console.log('🔧 Machine filter:', filters.selectedMachine, '(mapped to equipmentId)');
      }

      // Add typeOfService for drill-down functionality
      if (selectedServiceTypeForDrillDown) {
        apiFilters.typeOfService = selectedServiceTypeForDrillDown;
        console.log('🔍 Service Type drill-down:', selectedServiceTypeForDrillDown);
      }

      console.log('📤 Maintenance API filters:', apiFilters);
      console.log('📋 All filters from context:', {
        selectedCompany: filters.selectedCompany,
        selectedLocation: filters.selectedLocation,
        selectedMachine: filters.selectedMachine,
        selectedShift: filters.selectedShift
      });

      const response = await maintenanceAnalyticsAPI.getAnalytics(apiFilters);

      if (response && response.success) {
        console.log('✅ Maintenance analytics data received:', response);

        // Extract and transform summary data
        if (response.summary) {
          setKeyMetrics({
            repairs: { value: response.summary.repairs || 0, unit: 'Repairs' },
            sumOfRepair: { value: response.summary.sumOfRepairHours || 0, unit: 'Hours' },
            mtbf: { value: response.summary.mtbf || 0, unit: 'Hours' },
            mdt: { value: response.summary.mdt || 0, unit: 'Hours' }
          });
          console.log('✅ Key metrics updated:', response.summary);
        }

        // Extract and transform graphs data
        if (response.graphs) {
          // Transform repairHoursAndMDTByEquipment
          if (response.graphs.repairHoursAndMDTByEquipment && Array.isArray(response.graphs.repairHoursAndMDTByEquipment)) {
            const transformed = response.graphs.repairHoursAndMDTByEquipment.map(item => ({
              name: item.equipment_no || '',
              equipmentId: item.equipmentId || item.equipment_id || item.id || item.equipment_no,
              repairHours: item.sum_repair_hours || 0,
              avgRepair: item.avg_repair_hours || 0
            }));
            setSumOfRepairHoursByEquipment(transformed);
            console.log('✅ Repair hours by equipment transformed:', transformed.length, 'items');
          }

          // Transform repairHoursByTypeOfService
          if (response.graphs.repairHoursByTypeOfService && Array.isArray(response.graphs.repairHoursByTypeOfService)) {
            const total = response.graphs.repairHoursByTypeOfService.reduce((sum, item) => sum + (item.sum_repair_hours || 0), 0);
            const transformed = response.graphs.repairHoursByTypeOfService
              .sort((a, b) => (b.sum_repair_hours || 0) - (a.sum_repair_hours || 0)) // Sort by value for consistent color assignment
              .map((item, index) => ({
                name: item.type_of_service || 'Unknown',
                value: item.sum_repair_hours || 0,
                percentage: total > 0 ? ((item.sum_repair_hours || 0) / total * 100).toFixed(2) : 0,
                fill: item.type_of_service === 'Unknown' || !item.type_of_service ? '#94a3b8' : barColors[index % barColors.length]
              }));
            setSumOfRepairHoursByServiceType(transformed);
            console.log('✅ Repair hours by service type transformed:', transformed.length, 'items');
          }

          // Transform mtbfByEquipment
          if (response.graphs.mtbfByEquipment && Array.isArray(response.graphs.mtbfByEquipment)) {
            const transformed = response.graphs.mtbfByEquipment.map((item, index) => ({
              name: item.equipment_no || '',
              equipment: item.equipment_no || '', // Also add equipment key for Y-axis
              equipmentId: item.equipmentId || item.equipment_id || item.id || item.equipment_no,
              mtbf: item.mtbf || 0
            }));
            setMtbfByEquipmentNo(transformed);
            console.log('✅ MTBF by equipment transformed:', transformed.length, 'items');
          }

          // Transform mdtByEquipment
          if (response.graphs.mdtByEquipment && Array.isArray(response.graphs.mdtByEquipment)) {
            const transformed = response.graphs.mdtByEquipment.map((item, index) => ({
              name: item.equipment_no || '',
              equipment: item.equipment_no || '', // Also add equipment key for Y-axis
              equipmentId: item.equipmentId || item.equipment_id || item.id || item.equipment_no,
              mdt: item.mdt || 0
            }));
            setMdtByEquipmentNo(transformed);
            console.log('✅ MDT by equipment transformed:', transformed.length, 'items');
          }

          // Transform mdtByTypeOfService
          if (response.graphs.mdtByTypeOfService && Array.isArray(response.graphs.mdtByTypeOfService)) {
            const transformed = response.graphs.mdtByTypeOfService.map(item => ({
              name: item.type_of_service || 'Unknown',
              mdt: item.mdt || 0
            }));
            setMdtByServiceType(transformed);
            console.log('✅ MDT by service type transformed:', transformed.length, 'items');
          }

          // Transform mtbfByTypeOfService
          if (response.graphs.mtbfByTypeOfService && Array.isArray(response.graphs.mtbfByTypeOfService)) {
            const transformed = response.graphs.mtbfByTypeOfService.map(item => ({
              name: item.type_of_service || 'Unknown',
              mtbf: item.mtbf || 0
            }));
            setMtbfByServiceType(transformed);
            console.log('✅ MTBF by service type transformed:', transformed.length, 'items');
          }

          // Helper to transform date-based chart data supporting both:
          // - New API structure: { current: [...], forecast: [...] }
          // - Legacy structure: flat array [...]
          const transformDateChartData = (graphData, valueKey) => {
            if (!graphData) return [];

            // New API structure with current and forecast arrays
            if (graphData.current && Array.isArray(graphData.current)) {
              const currentData = graphData.current;
              const forecastData = graphData.forecast || [];

              // Combine current and forecast data by date
              const allDates = new Set([
                ...currentData.map(item => item.date),
                ...forecastData.map(item => item.date)
              ]);

              return Array.from(allDates).map(date => {
                const currentItem = currentData.find(item => item.date === date);
                const forecastItem = forecastData.find(item => item.date === date);
                return {
                  date,
                  current: currentItem ? (currentItem[valueKey] || 0) : null,
                  forecast: forecastItem ? (forecastItem[valueKey] || 0) : null
                };
              });
            }

            // Legacy structure (direct array)
            if (Array.isArray(graphData)) {
              return graphData.map(item => ({
                date: item.date || '',
                current: item[valueKey] || 0,
                forecast: null
              }));
            }

            return [];
          };

          // Transform uptime by different resolutions
          const upDataMap = { day: [], month: [], quarter: [], year: [] };
          upDataMap.day = transformDateChartData(response.graphs.uptimeByDay || response.graphs.uptimeByDate, 'uptime');
          upDataMap.month = transformDateChartData(response.graphs.uptimeByMonth, 'uptime');
          upDataMap.quarter = transformDateChartData(response.graphs.uptimeByQuarter, 'uptime');
          upDataMap.year = transformDateChartData(response.graphs.uptimeByYear, 'uptime');

          setUptimeDataMap(upDataMap);
          setSumOfUptimeByDate(upDataMap[sumOfUptimeResolution]);
          console.log('✅ Uptime data maps updated');

          // Transform mdtByDate
          const mdtData = transformDateChartData(response.graphs.mdtByDate, 'mdt');
          if (mdtData.length > 0) {
            setMdtByDate(mdtData);
            console.log('✅ MDT by date transformed:', mdtData.length, 'items');
          }

          // Transform MTBF by different resolutions
          const bfDataMap = { day: [], month: [], quarter: [], year: [] };
          bfDataMap.day = transformDateChartData(response.graphs.mtbfByDay || response.graphs.mtbfByDate, 'mtbf');
          bfDataMap.month = transformDateChartData(response.graphs.mtbfByMonth, 'mtbf');
          bfDataMap.quarter = transformDateChartData(response.graphs.mtbfByQuarter, 'mtbf');
          bfDataMap.year = transformDateChartData(response.graphs.mtbfByYear, 'mtbf');

          setMtbfDataMap(bfDataMap);
          setMtbfByDate(bfDataMap[mtbfResolution]);
          console.log('✅ MTBF data maps updated');

          // Update available machines in context for the FilterBar
          const equipmentGraphs = [
            response.graphs.repairHoursAndMDTByEquipment,
            response.graphs.mtbfByEquipment,
            response.graphs.mdtByEquipment
          ];

          const machinesFromData = [];
          const seenMachineIds = new Set();

          equipmentGraphs.forEach(graph => {
            if (graph && Array.isArray(graph)) {
              graph.forEach(item => {
                // Use equipment_no as name, and check for equipment_id/id
                const machineId = item.equipment_id || item.id || item.equipment_no;
                const machineName = item.equipment_no || `Machine ${machineId}`;

                if (machineId && !seenMachineIds.has(machineId)) {
                  seenMachineIds.add(machineId);
                  machinesFromData.push({
                    machineId: machineId,
                    machineName: machineName
                  });
                }
              });
            }
          });

          if (machinesFromData.length > 0) {
            console.log('🔧 Updating available machines in context:', machinesFromData.length);
            setAvailableMachines(machinesFromData);
          }
        }

        // Store the raw response data
        setMaintenanceData(response);

        // Mark initial data as loaded after first successful API call
        if (!maintenanceInitialDataLoaded) {
          setMaintenanceInitialDataLoaded(true);
          prevManualTriggerRef.current = manualTrigger; // Initialize ref
          console.log('✅ Initial data loaded - subsequent filter changes require manual trigger');
        } else {
          // Update ref after successful manual trigger API call
          prevManualTriggerRef.current = manualTrigger;
        }

        prevSelectedEquipmentIdRef.current = selectedEquipmentIdForDrillDown;
        prevSelectedEquipmentRef.current = selectedEquipmentForDrillDown;
        prevSelectedServiceTypeRef.current = selectedServiceTypeForDrillDown;

        console.log('✅ Maintenance analytics data loaded and transformed successfully');
      } else {
        console.warn('⚠️ Maintenance analytics API returned no data or error');
      }
    } catch (error) {
      console.error('❌ Error fetching maintenance analytics:', error);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [filters, maintenanceInitialDataLoaded, manualTrigger, selectedEquipmentIdForDrillDown, selectedEquipmentForDrillDown, selectedServiceTypeForDrillDown, setAvailableMachines]);

  // Fetch maintenance data - auto-trigger on initial load, manual trigger after that
  useEffect(() => {
    // Only fetch if we have a company selected
    const companyId = filters.selectedCompany ||
      localStorage.getItem('companyId') ||
      sessionStorage.getItem('companyId');

    if (!companyId) {
      console.log('⏳ Waiting for company selection to fetch maintenance data...');
      return;
    }

    // On initial load (when Maintenance tab is first accessed), automatically trigger API with default date range
    if (!maintenanceInitialDataLoaded) {
      // Check if we've already made the initial call
      if (initialCallMadeRef.current) {
        console.log('⏸️ Initial API call already made, skipping duplicate');
        return;
      }
      // Mark that initial call is being made (set synchronously before async operations)
      initialCallMadeRef.current = true;
      console.log('🔄 Initial load - automatically fetching maintenance data with default date range');
      prevManualTriggerRef.current = manualTrigger; // Initialize ref
      fetchMaintenanceData();
      return;
    }

    // After initial load, only trigger API if manual trigger is activated (arrow button clicked)
    if (maintenanceInitialDataLoaded) {
      // Check if drill-down selection changed
      const drillDownChanged = selectedEquipmentIdForDrillDown !== prevSelectedEquipmentIdRef.current ||
        selectedEquipmentForDrillDown !== prevSelectedEquipmentRef.current ||
        selectedServiceTypeForDrillDown !== prevSelectedServiceTypeRef.current;

      // Check if manualTrigger actually changed (arrow button was clicked)
      const manualTriggerActivated = manualTrigger !== prevManualTriggerRef.current;

      if (!drillDownChanged && !manualTriggerActivated) {
        console.log('⏸️ Skipping API call - no drill-down change or manual trigger');
        return;
      }

      if (drillDownChanged) {
        console.log('🔍 Drill-down selection changed - automatically triggering API call');
      } else {
        console.log('🔵 Manual trigger activated - proceeding with API call');
      }

      fetchMaintenanceData();
    }
  }, [
    filters.selectedCompany,
    filters.dateRange?.from?.getTime(),
    filters.dateRange?.to?.getTime(),
    filters.selectedMachine,
    filters.selectedLocation,
    filters.selectedShift,
    selectedEquipmentIdForDrillDown,
    selectedEquipmentForDrillDown,
    selectedServiceTypeForDrillDown,
    manualTrigger,
    fetchMaintenanceData
  ]);

  // Handler for equipment drill-down
  const handleEquipmentDrillDown = useCallback((equipmentName, equipmentId) => {
    console.log('🔍 Drill-down clicked for equipment:', equipmentName, 'ID:', equipmentId);
    setSelectedEquipmentForDrillDown(equipmentName);
    setSelectedEquipmentIdForDrillDown(equipmentId);
  }, []);

  // Handler for service type drill-down
  const handleServiceTypeDrillDown = useCallback((serviceType) => {
    console.log('🔍 Drill-down clicked for service type:', serviceType);
    setSelectedServiceTypeForDrillDown(serviceType);
  }, []);

  // Handler to clear drill-down
  const handleClearDrillDown = useCallback(() => {
    console.log('🔍 Clearing all drill-downs');
    setSelectedEquipmentForDrillDown(null);
    setSelectedEquipmentIdForDrillDown(null);
    setSelectedServiceTypeForDrillDown(null);
  }, []);

  // Handler to clear service type drill-down
  const handleClearServiceTypeDrillDown = useCallback(() => {
    console.log('🔍 Clearing service type drill-down');
    setSelectedServiceTypeForDrillDown(null);
  }, []);

  // Sync displayed chart data when resolution changes
  useEffect(() => {
    if (uptimeDataMap[sumOfUptimeResolution]) {
      setSumOfUptimeByDate(uptimeDataMap[sumOfUptimeResolution]);
    }
  }, [sumOfUptimeResolution, uptimeDataMap]);

  useEffect(() => {
    if (mtbfDataMap[mtbfResolution]) {
      setMtbfByDate(mtbfDataMap[mtbfResolution]);
    }
  }, [mtbfResolution, mtbfDataMap]);

  // Format donut chart label - improved visibility for small cards
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, value, payload }) => {
    // Hide labels for small segments (e.g., < 5%) to prevent cluster overlap in small dashboard cards
    if (value === 0 || percent < 0.05) return null;

    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 15;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    const percentage = payload?.percentage || `${(percent * 100).toFixed(1)}%`;
    const displayValue = value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toFixed(2);

    return (
      <text
        x={x}
        y={y}
        fill="#374151"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        style={{ fontSize: '9px', fontWeight: 600 }}
      >
        {`${displayValue} (${percentage})`}
      </text>
    );
  };

  // Chart configurations - maps chart IDs to their properties
  const chartConfigs = {
    'sum-of-repair-hours-by-equipment': {
      title: 'Sum of Repair Hours and Avg repair measure line by Equipment No',
      dataKey: 'sumOfRepairHoursByEquipment',
      // apiEndpoint: '/api/maintenance/sum-of-repair-hours-by-equipment',
      chartType: 'composed',
      chartProps: {
        barCategoryGap: '10%',
        xAxis: { dataKey: 'name', tick: { fontSize: 12 }, interval: 0, label: { value: 'Equipment No', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } } },
        yAxis: { tick: { fontSize: 12 }, label: { value: 'Sum of Repair Hours and Avg Repair...', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } } },
        referenceLine: { y: 100, stroke: '#ef4444', strokeDasharray: '3 3' },
        bars: [
          {
            dataKey: 'repairHours',
            radius: [4, 4, 0, 0],
            barSize: 34.94,
            fill: '#ec4899' // Pink
          }
        ],
        lines: [
          { dataKey: 'avgRepair', stroke: '#ef4444', strokeWidth: 2, dot: false }
        ]
      }
    },
    'sum-of-repair-hours-by-service-type': {
      title: 'Sum of Repair Hours by Type of Service',
      dataKey: 'sumOfRepairHoursByServiceType',
      // apiEndpoint: '/api/maintenance/sum-of-repair-hours-by-service-type',
      chartType: 'pie',
      chartProps: {
        innerRadius: 0,
        outerRadius: 80,
        dataKey: 'value',
        showLegend: true
      }
    },
    'mtbf-by-equipment-no': {
      title: 'MTBF(hours) by Equipment No',
      dataKey: 'mtbfByEquipmentNo',
      // apiEndpoint: '/api/maintenance/mtbf-by-equipment-no',
      chartType: 'horizontalBar',
      chartProps: {
        layout: 'vertical',
        barCategoryGap: '10%',
        dataKey: 'mtbf',
        radius: [0, 4, 4, 0],
        barSize: 34.94,
        xAxis: {
          type: 'number',
          tick: { fontSize: 12 },
          domain: [0, 800],
          label: { value: 'MTBF (hours)', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } }
        },
        yAxis: {
          type: 'category',
          dataKey: 'equipment',
          tick: { fontSize: 12 },
          label: { value: 'Equipment No', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } }
        },
        useCellColors: false,
        fill: '#a78bfa' // Purple
      }
    },
    'mdt-by-equipment-no': {
      title: 'MDT(hours) by Equipment No',
      dataKey: 'mdtByEquipmentNo',
      // apiEndpoint: '/api/maintenance/mdt-by-equipment-no',
      chartType: 'horizontalBar',
      chartProps: {
        layout: 'vertical',
        barCategoryGap: '10%',
        dataKey: 'mdt',
        radius: [0, 4, 4, 0],
        barSize: 34.94,
        xAxis: {
          type: 'number',
          tick: { fontSize: 12 },
          domain: [0, 2.5],
          label: { value: 'MDT (hours)', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } }
        },
        yAxis: {
          type: 'category',
          dataKey: 'equipment',
          tick: { fontSize: 12 },
          label: { value: 'Equipment No', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } }
        },
        useCellColors: false,
        fill: '#06b6d4' // Cyan
      }
    },
    'mdt-by-service-type': {
      title: 'MDT(hours) by Type of Service',
      dataKey: 'mdtByServiceType',
      // apiEndpoint: '/api/maintenance/mdt-by-service-type',
      chartType: 'horizontalBar',
      chartProps: {
        layout: 'vertical',
        barCategoryGap: '10%',
        dataKey: 'mdt',
        radius: [0, 4, 4, 0],
        barSize: 34.94,
        xAxis: {
          type: 'number',
          tick: { fontSize: 12 },
          tickFormatter: (value) => value >= 1000 ? `${value / 1000}K` : value.toString(),
          domain: [0, 100],
          label: { value: 'MDT (hours)', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } }
        },
        yAxis: {
          type: 'category',
          dataKey: 'name',
          tick: { fontSize: 12 },
          label: { value: 'Type of Service', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } }
        },
        useCellColors: false,
        fill: '#10b981' // Emerald
      }
    },
    'mtbf-by-service-type': {
      title: 'MTBF(hours) by Type of Service',
      dataKey: 'mtbfByServiceType',
      // apiEndpoint: '/api/maintenance/mtbf-by-service-type',
      chartType: 'horizontalBar',
      chartProps: {
        layout: 'vertical',
        barCategoryGap: '10%',
        dataKey: 'mtbf',
        radius: [0, 4, 4, 0],
        barSize: 34.94,
        xAxis: {
          type: 'number',
          tick: { fontSize: 12 },
          tickFormatter: (value) => value >= 1000 ? `${value / 1000}K` : value.toString(),
          domain: [0, 100],
          label: { value: 'MTBF (hours)', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 12 } }
        },
        yAxis: {
          type: 'category',
          dataKey: 'name',
          tick: { fontSize: 12 },
          label: { value: 'Type of Service', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } }
        },
        useCellColors: false,
        fill: '#f97316' // Orange
      }
    },
    'sum-of-uptime-by-date': {
      title: 'Sum of Uptime by Year, Quarter, Month and Day',
      dataKey: 'sumOfUptimeByDate',
      // apiEndpoint: '/api/maintenance/sum-of-uptime-by-date',
      chartType: 'line',
      chartProps: {
        xAxis: { dataKey: 'date', tick: { fontSize: 12 }, label: { value: 'Date', position: 'insideBottom', offset: -1, style: { textAnchor: 'middle', fontSize: 12 } } },
        yAxis: {
          tick: { fontSize: 12 },
          tickFormatter: (value) => `${value}bn`,
          domain: [0, 2],
          label: { value: 'Sum of Uptime', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12 } }
        },
        lines: [
          { dataKey: 'current', stroke: '#3b82f6', strokeWidth: 2, dot: { r: 4 }, name: 'Current' },
          { dataKey: 'forecast', stroke: '#f97316', strokeWidth: 2, strokeDasharray: '5 5', dot: { r: 4 }, name: 'Forecast' }
        ],
        referenceLine: { y: 1.7, stroke: '#000', strokeDasharray: '3 3' }
      }
    },
    'mdt-by-date': {
      title: 'MDT(Hours) by Date',
      dataKey: 'mdtByDate',
      // apiEndpoint: '/api/maintenance/mdt-by-date',
      chartType: 'line',
      chartProps: {
        xAxis: { dataKey: 'date', tick: { fontSize: 12 }, label: { value: 'Date', position: 'insideBottom', offset: -1, style: { textAnchor: 'middle', fontSize: 12 } } },
        yAxis: {
          tick: { fontSize: 12 },
          domain: [0, 5],
          label: { value: 'MDT (hours)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12 } }
        },
        lines: [
          { dataKey: 'current', stroke: '#3b82f6', strokeWidth: 2, dot: { r: 4 }, name: 'Current' },
          { dataKey: 'forecast', stroke: '#f97316', strokeWidth: 2, strokeDasharray: '5 5', dot: { r: 4 }, name: 'Forecast' }
        ],
        referenceLine: { y: 4.0, stroke: '#000', strokeDasharray: '3 3' }
      }
    },
    'mtbf-by-date': {
      title: 'MTBF (Hours) by Year, Quarter, Month and Day',
      dataKey: 'mtbfByDate',
      // apiEndpoint: '/api/maintenance/mtbf-by-date',
      chartType: 'line',
      chartProps: {
        xAxis: { dataKey: 'date', tick: { fontSize: 12 }, label: { value: 'Date', position: 'insideBottom', offset: -1, style: { textAnchor: 'middle', fontSize: 12 } } },
        yAxis: {
          tick: { fontSize: 12 },
          tickFormatter: (value) => `${value}K`,
          domain: [0, 10],
          label: { value: 'MTBF (hours)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12 } }
        },
        lines: [
          { dataKey: 'current', stroke: '#3b82f6', strokeWidth: 2, dot: { r: 4 }, name: 'Current' },
          { dataKey: 'forecast', stroke: '#f97316', strokeWidth: 2, strokeDasharray: '5 5', dot: { r: 4 }, name: 'Forecast' }
        ],
        referenceLine: { y: 5.5, stroke: '#000', strokeDasharray: '3 3' }
      }
    }
  };

  // Handler for maximize button
  const handleMaximize = useCallback((chartId) => {
    const config = chartConfigs[chartId];
    if (!config) return;
    setMaximizedChartId(chartId);
  }, []);

  const handleCloseModal = useCallback(() => {
    setMaximizedChartId(null);
    setModalEquipmentFilter('all'); // Reset equipment/service filter when closing modal
    setModalSortOrder('low-high'); // Reset sort order when closing modal
  }, []);

  // Get unique equipment/service type list for the current chart
  const getEquipmentListForChart = useCallback((chartId) => {
    let dataSource = [];
    let nameField = 'name';

    if (chartId === 'sum-of-repair-hours-by-equipment') {
      dataSource = sumOfRepairHoursByEquipment || [];
    } else if (chartId === 'sum-of-repair-hours-by-service-type') {
      dataSource = sumOfRepairHoursByServiceType || [];
    } else if (chartId === 'mtbf-by-equipment-no') {
      dataSource = mtbfByEquipmentNo || [];
    } else if (chartId === 'mdt-by-equipment-no') {
      dataSource = mdtByEquipmentNo || [];
    } else if (chartId === 'mdt-by-service-type') {
      dataSource = mdtByServiceType || [];
    } else if (chartId === 'mtbf-by-service-type') {
      dataSource = mtbfByServiceType || [];
    }

    if (chartId === 'mtbf-by-equipment-no' || chartId === 'mdt-by-equipment-no') {
      nameField = 'equipment';
    }

    return [...new Set(dataSource.map(item => item[nameField]).filter(Boolean))];
  }, [sumOfRepairHoursByEquipment, sumOfRepairHoursByServiceType, mtbfByEquipmentNo, mdtByEquipmentNo, mdtByServiceType, mtbfByServiceType]);

  // Get filter label based on chart type
  const getFilterLabelForChart = (chartId) => {
    const serviceTypeCharts = ['sum-of-repair-hours-by-service-type', 'mdt-by-service-type', 'mtbf-by-service-type'];
    if (serviceTypeCharts.includes(chartId)) {
      return 'All Service Types';
    }
    return 'All Equipment';
  };

  // Format donut chart label for maximized view - with smart spacing to prevent overlap
  const labelPositions = useRef([]);

  // Render maximized chart component
  const renderMaximizedChart = (chartId) => {
    const config = chartConfigs[chartId];
    if (!config) return null;

    // Get data based on chart dataKey
    let data = [];
    switch (config.dataKey) {
      case 'sumOfRepairHoursByEquipment':
        data = sumOfRepairHoursByEquipment;
        break;
      case 'sumOfRepairHoursByServiceType':
        data = sumOfRepairHoursByServiceType;
        break;
      case 'mtbfByEquipmentNo':
        data = mtbfByEquipmentNo;
        break;
      case 'mdtByEquipmentNo':
        data = mdtByEquipmentNo;
        break;
      case 'mdtByServiceType':
        data = mdtByServiceType;
        break;
      case 'mtbfByServiceType':
        data = mtbfByServiceType;
        break;
      case 'sumOfUptimeByDate':
        data = sumOfUptimeByDate;
        break;
      case 'mdtByDate':
        data = mdtByDate;
        break;
      case 'mtbfByDate':
        data = mtbfByDate;
        break;
      default:
        data = [];
    }

    // Apply equipment/service type filter if not "all"
    if (modalEquipmentFilter !== 'all') {
      if (config.chartType === 'pie') {
        // For pie charts, show selected item with actual proportion + "Others"
        const selectedItem = data.find(item => (item.name || item.equipment) === modalEquipmentFilter);
        if (selectedItem) {
          const otherItems = data.filter(item => (item.name || item.equipment) !== modalEquipmentFilter);
          const otherValue = otherItems.reduce((sum, item) => sum + (item.value || 0), 0);

          // Create data with selected item and "Others" to maintain proportions
          data = [
            selectedItem,
            {
              name: 'Others (Rest)',
              value: otherValue,
              fill: '#d1d5db', // Gray color for others
              percentage: null // Will be calculated in label renderer
            }
          ].filter(item => item.value > 0);
        }
      } else {
        data = data.filter(item => (item.name || item.equipment) === modalEquipmentFilter);
      }
    }

    // Apply sorting for pie charts
    if (config.chartType === 'pie' && data.length > 0) {
      const sortKey = config.chartProps?.dataKey || 'value';
      data = [...data].sort((a, b) => {
        const aValue = a[sortKey] || 0;
        const bValue = b[sortKey] || 0;
        return modalSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
      });
    }

    // Apply sorting for bar charts (composed, horizontalBar) in maximized view
    if ((config.chartType === 'composed' || config.chartType === 'horizontalBar') && data.length > 0) {
      let sortDataKey;
      if (config.chartType === 'composed' && config.chartProps.bars && config.chartProps.bars.length > 0) {
        sortDataKey = config.chartProps.bars[0].dataKey;
      } else if (config.chartType === 'horizontalBar') {
        sortDataKey = config.chartProps.dataKey;
      }

      if (sortDataKey) {
        data = [...data].sort((a, b) => {
          const aValue = Number(a[sortDataKey]) || 0;
          const bValue = Number(b[sortDataKey]) || 0;
          return modalSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
        });
      }
    }

    // Apply sorting for MDT by Date line chart in maximized view
    if (chartId === 'mdt-by-date' && data.length > 0) {
      data = [...data].sort((a, b) => {
        const aVal = Number(a.current) || 0;
        const bVal = Number(b.current) || 0;
        return modalSortOrder === 'low-high' ? aVal - bVal : bVal - aVal;
      });
    }

    if (config.chartType === 'composed') {
      const props = config.chartProps;
      return (
        <ResponsiveContainer width="100%" height="100%" key={`composed-${chartId}-${modalSortOrder}`}>
          <ComposedChart data={data} barCategoryGap={props.barCategoryGap} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis {...props.xAxis} />
            <YAxis {...props.yAxis} />
            <Tooltip cursor={false} />
            {props.referenceLine && (
              <ReferenceLine
                y={props.referenceLine.y}
                stroke={props.referenceLine.stroke}
                strokeDasharray={props.referenceLine.strokeDasharray}
              />
            )}
            {props.bars.map((bar, index) => (
              <Bar
                key={index}
                dataKey={bar.dataKey}
                radius={bar.radius}
                barSize={48} // Reduced from fixed larger value
                fill={bar.fill || '#3b82f6'}
                activeBar={false}
              >
                {data.map((entry, idx) => (
                  <Cell
                    key={`cell-${idx}`}
                    fill={entry.equipmentId === selectedEquipmentIdForDrillDown ? "#1e40af" : (bar.fill || '#3b82f6')}
                  />
                ))}
              </Bar>
            ))}
            {props.lines.map((line, index) => (
              <Line
                key={index}
                type="monotone"
                dataKey={line.dataKey}
                stroke={line.stroke}
                strokeWidth={line.strokeWidth}
                dot={line.dot}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      );
    } else if (config.chartType === 'pie') {
      const props = config.chartProps;
      // Use responsive radius for maximized view
      const mInnerRadius = 0;
      // Dynamic radius: larger for 2xl screens (TVs), standard for desktop
      const isTV = typeof window !== 'undefined' && window.innerWidth >= 1920;
      const mOuterRadius = isTV ? 180 : 130;

      // Calculate total for percentages
      const total = data.reduce((sum, d) => sum + (d.value || 0), 0);

      // Custom label renderer with automatic line extension for better spacing
      const renderPieLabel = (labelProps) => {
        const { cx, cy, midAngle, outerRadius, value, payload, index } = labelProps;
        if (!value || value === 0) return null;

        const RADIAN = Math.PI / 180;
        const percent = value / total;

        // Calculate base extension - smaller segments get longer lines
        const baseExtension = isTV ? 25 : 15;
        const extraExtension = percent < 0.01 ? 60 : percent < 0.05 ? 40 : percent < 0.1 ? 30 : 15;
        const totalExtension = baseExtension + extraExtension + (index % 3) * 10; // Stagger lines

        const radius = outerRadius + totalExtension;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        // Line start point (at pie edge)
        const lineStartX = cx + (outerRadius + 5) * Math.cos(-midAngle * RADIAN);
        const lineStartY = cy + (outerRadius + 5) * Math.sin(-midAngle * RADIAN);

        // Mid point for the elbow
        const midRadius = outerRadius + totalExtension - 20;
        const midX = cx + midRadius * Math.cos(-midAngle * RADIAN);
        const midY = cy + midRadius * Math.sin(-midAngle * RADIAN);

        const isRightSide = x > cx;
        const percentage = payload?.percentage || `${(percent * 100).toFixed(2)}%`;
        const displayValue = value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toFixed(2);

        return (
          <g>
            {/* Connecting line with elbow */}
            <path
              d={`M ${lineStartX} ${lineStartY} L ${midX} ${midY} L ${x} ${y}`}
              fill="none"
              stroke="#9ca3af"
              strokeWidth={1}
            />
            {/* Small dot at the segment edge */}
            <circle cx={lineStartX} cy={lineStartY} r={2} fill="#9ca3af" />
            {/* Label text */}
            <text
              x={x + (isRightSide ? 5 : -5)}
              y={y}
              fill="#374151"
              textAnchor={isRightSide ? 'start' : 'end'}
              dominantBaseline="central"
              style={{ fontSize: '13px', fontWeight: 600 }} // Increased from 10px
            >
              {`${displayValue} (${percentage})`}
            </text>
          </g>
        );
      };

      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={mInnerRadius}
              outerRadius={mOuterRadius}
              dataKey={props.dataKey}
              label={renderPieLabel}
              labelLine={false}
              paddingAngle={1}
              stroke="#fff"
              strokeWidth={1}
              onClick={(data) => { if (data && data.name && data.name !== 'Others (Rest)') handleServiceTypeDrillDown(data.name); }}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.name === selectedServiceTypeForDrillDown ? "#1e40af" : entry.fill}
                  style={{ cursor: entry.name === 'Others (Rest)' ? 'default' : 'pointer' }}
                />
              ))}
            </Pie>
            <Tooltip />
            {props.showLegend && (
              <Legend
                verticalAlign="bottom"
                height={50}
                onClick={(e) => {
                  const itemName = e.value;
                  if (itemName && itemName !== 'Others (Rest)') {
                    // Toggle selection: if already selected, go back to "all"
                    if (modalEquipmentFilter === itemName) {
                      setModalEquipmentFilter('all');
                    } else {
                      setModalEquipmentFilter(itemName);
                    }
                  }
                }}
                wrapperStyle={{ cursor: 'pointer' }}
                formatter={(value, entry) => {
                  const isSelected = modalEquipmentFilter === value;
                  const isOthers = value === 'Others (Rest)';
                  return (
                    <span
                      className={`text-xs font-medium ${isSelected
                        ? 'text-blue-600 font-bold underline'
                        : isOthers
                          ? 'text-gray-400'
                          : 'text-gray-700 hover:text-blue-500'
                        }`}
                      style={{ cursor: isOthers ? 'default' : 'pointer' }}
                    >
                      {entry.payload.name}
                    </span>
                  );
                }}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
      );
    } else if (config.chartType === 'horizontalBar') {
      const props = config.chartProps;
      // Increased height per item ensures bars have enough space (100px per item)
      const totalHeight = data.length > 5 ? Math.max(600, data.length * 100) : '100%';
      const xAxisHeight = 60;
      const yAxisWidth = props.yAxis?.label ? 180 : 150;

      // Extract axis labels
      const xAxisPropsWithoutLabel = { ...props.xAxis };
      delete xAxisPropsWithoutLabel.label;
      const yAxisPropsWithoutLabel = { ...props.yAxis };
      delete yAxisPropsWithoutLabel.label;

      return (
        <div className="w-full h-full flex flex-col overflow-hidden relative">
          {/* Fixed Y-axis label - positioned absolutely on the left */}
          {props.yAxis?.label && (
            <div
              className="absolute left-0 top-0 bottom-0 z-30 pointer-events-none flex items-center"
              style={{
                width: '60px',
                paddingLeft: '15px',
                paddingBottom: `${xAxisHeight}px`
              }}
            >
              <div
                style={{
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed',
                  transform: 'rotate(180deg)',
                  fontSize: '13px', // Increased from 12px
                  fontWeight: 600, // Increased from 500
                  color: '#374151',
                  whiteSpace: 'nowrap'
                }}
              >
                {props.yAxis.label.value || 'Y-Axis'}
              </div>
            </div>
          )}

          {/* Scrollable Data Area */}
          <div
            className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar"
            style={{ marginLeft: props.yAxis?.label ? '60px' : '0', marginBottom: `${xAxisHeight}px` }}
          >
            <div style={{ height: totalHeight, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data}
                  layout="vertical"
                  margin={{ ...props.margin, top: 20, bottom: 0, left: 0 }}
                  barCategoryGap="20%"
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                  <YAxis {...yAxisPropsWithoutLabel} width={yAxisWidth} />
                  {/* Hidden XAxis so the Bar chart knows the numerical scale */}
                  <XAxis {...xAxisPropsWithoutLabel} hide />
                  <Tooltip cursor={false} />
                  <Bar
                    dataKey={props.dataKey}
                    fill={props.fill || '#3b82f6'}
                    radius={props.radius}
                    barSize={48} // Reduced from 62.29
                    activeBar={false}
                    onClick={(data) => {
                      if (data && data.name) {
                        const isServiceTypeChart = ['mdt-by-service-type', 'mtbf-by-service-type', 'sum-of-repair-hours-by-service-type'].includes(maximizedChartId);
                        if (isServiceTypeChart) {
                          handleServiceTypeDrillDown(data.name);
                        } else if (['mtbf-by-equipment-no', 'mdt-by-equipment-no', 'sum-of-repair-hours-by-equipment'].includes(maximizedChartId)) {
                          handleEquipmentDrillDown(data.name, data.equipmentId || data.name);
                        }
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {data.map((entry, index) => {
                      let isHighlighted = false;
                      if (entry.equipmentId && entry.equipmentId === selectedEquipmentIdForDrillDown) {
                        isHighlighted = true;
                      } else if (entry.name && entry.name === selectedServiceTypeForDrillDown) {
                        isHighlighted = true;
                      }

                      return (
                        <Cell
                          key={`cell-${index}`}
                          fill={isHighlighted ? "#1e40af" : (props.fill || '#3b82f6')}
                          style={{ cursor: 'pointer' }}
                        />
                      );
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Sticky X-Axis with label */}
          <div
            className="w-full flex-shrink-0 relative"
            style={{
              height: `${xAxisHeight}px`,
              marginLeft: props.yAxis?.label ? '60px' : '0'
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={data} margin={{ ...props.margin, top: 0, left: 0 }}>
                <XAxis {...xAxisPropsWithoutLabel} orientation="bottom" height={xAxisHeight - 5} />
                {/* Hidden YAxis to ensure XAxis alignment matches the data chart */}
                <YAxis {...yAxisPropsWithoutLabel} width={yAxisWidth} hide />
                {/* Hidden Bar ensures XAxis scales correctly for the labels */}
                <Bar dataKey={props.dataKey} hide />
              </BarChart>
            </ResponsiveContainer>
            {/* Fixed X-axis label */}
            {props.xAxis?.label && (
              <div
                className="absolute bottom-0 left-0 right-0 flex items-center justify-center"
                style={{ height: '20px' }}
              >
                <div
                  style={{
                    fontSize: '14px', // Increased from 12px
                    fontWeight: 600, // Increased from 500
                    color: '#374151',
                    textAlign: 'center'
                  }}
                >
                  {props.xAxis.label.value}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    } else if (config.chartType === 'line') {
      const props = config.chartProps;
      // Determine colors for indicators based on chart type
      const currentColor = props.lines?.[0]?.stroke || '#3b82f6';
      const forecastColor = props.lines?.[1]?.stroke || '#f97316';

      return (
        <div className="w-full h-full flex flex-col">
          <div className="flex-1">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis {...props.xAxis} tick={{ ...(props.xAxis?.tick || {}), fontSize: 13 }} />
                <YAxis {...props.yAxis} tick={{ ...(props.yAxis?.tick || {}), fontSize: 13 }} />
                <Tooltip />
                {props.lines.map((line, index) => (
                  <Line
                    key={index}
                    type="monotone"
                    dataKey={line.dataKey}
                    stroke={line.stroke}
                    strokeWidth={line.strokeWidth}
                    strokeDasharray={line.strokeDasharray}
                    dot={line.dot}
                  />
                ))}
                {props.referenceLine && (
                  <ReferenceLine
                    y={props.referenceLine.y}
                    stroke={props.referenceLine.stroke}
                    strokeDasharray={props.referenceLine.strokeDasharray}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center justify-center gap-6 px-6 pb-2 pt-2">
            <div className="flex items-center text-xs text-gray-600">
              <span className="w-3 h-3 rounded mr-2 inline-block" style={{ backgroundColor: currentColor }}></span>
              Current
            </div>
            <div className="flex items-center text-xs text-gray-600">
              <span className="w-3 h-3 rounded mr-2 inline-block" style={{ backgroundColor: forecastColor }}></span>
              Forecast
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Reusable Maximize Button Component
  const MaximizeButton = ({ chartId, className = "p-2 hover:bg-gray-100 rounded-lg transition-colors" }) => (
    <button
      onClick={() => handleMaximize(chartId)}
      className={className}
      aria-label="Maximize chart"
    >
      <Maximize2 className="w-4 h-4 text-gray-600" />
    </button>
  );

  // Render header actions (dropdown selectors) for maximized charts
  const renderHeaderActions = (chartId) => {
    const config = chartConfigs[chartId];
    if (!config) return null;

    // Resolution selection for date-based line charts
    if (chartId === 'sum-of-uptime-by-date') {
      return (
        <Select value={sumOfUptimeResolution} onValueChange={setSumOfUptimeResolution}>
          <SelectTrigger className="w-[140px] h-9 text-xs bg-gray-50">
            <SelectValue placeholder="Resolution" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Day</SelectItem>
            <SelectItem value="month">Month</SelectItem>
            <SelectItem value="quarter">Quarter</SelectItem>
            <SelectItem value="year">Year</SelectItem>
          </SelectContent>
        </Select>
      );
    }

    if (chartId === 'mtbf-by-date') {
      return (
        <Select value={mtbfResolution} onValueChange={setMtbfResolution}>
          <SelectTrigger className="w-[140px] h-9 text-xs bg-gray-50">
            <SelectValue placeholder="Resolution" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="day">Day</SelectItem>
            <SelectItem value="month">Month</SelectItem>
            <SelectItem value="quarter">Quarter</SelectItem>
            <SelectItem value="year">Year</SelectItem>
          </SelectContent>
        </Select>
      );
    }

    if (chartId === 'mdt-by-date') {
      return (
        <Select value={modalSortOrder} onValueChange={setModalSortOrder}>
          <SelectTrigger className="w-[140px] h-9 text-xs bg-gray-50">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="low-high">Low to High</SelectItem>
            <SelectItem value="high-low">High to Low</SelectItem>
          </SelectContent>
        </Select>
      );
    }

    // Sort selection for bar and horizontal charts
    if (config.chartType === 'bar' || config.chartType === 'horizontalBar' || config.chartType === 'composed') {
      return (
        <Select value={modalSortOrder} onValueChange={setModalSortOrder}>
          <SelectTrigger className="w-[140px] h-9 text-xs bg-gray-50">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="low-high">Low to High</SelectItem>
            <SelectItem value="high-low">High to Low</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col min-h-full">
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl flex items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      )}

      {/* Key Metrics Section */}
      <div className="px-3 md:px-4 pt-2 pb-1 bg-gray-50">
        <div className="flex flex-wrap gap-2">
          {/* Repairs */}
          <div className="bg-white rounded-lg shadow-sm p-2 border border-gray-200 min-w-[140px] flex-1 h-[80px] flex flex-col justify-between">
            <h3 className="text-xs font-bold font-open-sans text-neutral-950 mb-2">Repairs</h3>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold font-open-sans text-gray-900">
                {keyMetrics.repairs.value.toLocaleString()}
              </span>
              <span className="text-[12px] mt-1 text-gray-500">{keyMetrics.repairs.unit}</span>
            </div>
          </div>

          {/* Sum of Repair */}
          <div className="bg-white rounded-lg shadow-sm p-2 border border-gray-200 min-w-[140px] flex-1 h-[80px] flex flex-col justify-between">
            <h3 className="text-xs font-bold font-open-sans text-neutral-950 mb-2">Sum of Repair</h3>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold font-open-sans text-gray-900">
                {keyMetrics.sumOfRepair.value.toFixed(2)}
              </span>
              <span className="text-[12px] mt-1 text-gray-500">{keyMetrics.sumOfRepair.unit}</span>
            </div>
          </div>

          {/* MTBF */}
          <div className="bg-white rounded-lg shadow-sm p-2 border border-gray-200 min-w-[140px] flex-1 h-[80px] flex flex-col justify-between">
            <h3 className="text-xs font-bold font-open-sans text-neutral-950 mb-2">MTBF</h3>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold font-open-sans text-gray-900">
                {keyMetrics.mtbf.value.toFixed(2)}
              </span>
              <span className="text-[12px] mt-1 text-gray-500">{keyMetrics.mtbf.unit}</span>
            </div>
          </div>

          {/* MDT */}
          <div className="bg-white rounded-lg shadow-sm p-2 border border-gray-200 min-w-[140px] flex-1 h-[80px] flex flex-col justify-between">
            <h3 className="text-xs font-bold font-open-sans text-neutral-950 mb-2">MDT</h3>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold font-open-sans text-gray-900">
                {keyMetrics.mdt.value.toFixed(2)}
              </span>
              <span className="text-[12px] mt-1 text-gray-500">{keyMetrics.mdt.unit}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Active Drill-down Filters - UI indication */}
      {(selectedEquipmentForDrillDown || selectedServiceTypeForDrillDown) && (
        <div className="px-3 md:px-4 py-2 bg-gray-50 flex items-center gap-2">
          <div className="text-[12px] font-medium text-gray-500">Active Drill-down:</div>
          {selectedEquipmentForDrillDown && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-2 py-0.5 rounded-full text-[11px] flex items-center gap-1.5 shadow-sm font-style-open-sans">
              <span className="font-bold">Equipment:</span> {selectedEquipmentForDrillDown}
              <button
                onClick={() => {
                  setSelectedEquipmentForDrillDown(null);
                  setSelectedEquipmentIdForDrillDown(null);
                }}
                className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                title="Clear equipment filter"
              >
                <X className="w-3 h-3 text-blue-700" />
              </button>
            </div>
          )}
          {selectedServiceTypeForDrillDown && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5 rounded-full text-[11px] flex items-center gap-1.5 shadow-sm font-style-open-sans">
              <span className="font-bold">Service Type:</span> {selectedServiceTypeForDrillDown}
              <button
                onClick={handleClearServiceTypeDrillDown}
                className="hover:bg-emerald-200 rounded-full p-0.5 transition-colors"
                title="Clear service type filter"
              >
                <X className="w-3 h-3 text-emerald-700" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Charts Section */}
      <div className="flex-1 px-3 md:px-4 pb-4 bg-gray-50">
        <div className="grid gap-3 pt-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
          {/* Chart 1: Sum of Repair Hours and Avg repair measure line by Equipment No */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold text-gray-900">Sum of Repair Hours and Avg repair measure line by Equipment No</h3>
              <div className="flex items-center gap-2">
                <Select
                  value={sortOrder['sum-of-repair-hours-by-equipment'] || 'low-high'}
                  onValueChange={(value) => {
                    setSortOrder(prev => ({ ...prev, 'sum-of-repair-hours-by-equipment': value }));
                  }}
                >
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="sum-of-repair-hours-by-equipment" />
              </div>
            </div>
            <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
              {/* Fixed Y-axis label */}
              <div className="absolute left-8 top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                  Sum of Repair Hours
                </div>
              </div>
              {/* Scrollable chart container */}
              <div className="flex-1 overflow-x-auto overflow-y-hidden relative chart-scroll" style={{ marginLeft: '60px', marginBottom: '-19px' }}>
                {(() => {
                  let data = sumOfRepairHoursByEquipment || [];
                  const currentSortOrder = sortOrder['sum-of-repair-hours-by-equipment'] || 'low-high';
                  if (data.length > 0) {
                    data = [...data].sort((a, b) => {
                      const aValue = Number(a.repairHours) || 0;
                      const bValue = Number(b.repairHours) || 0;
                      return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                    });
                  }
                  const minChartWidth = Math.max(512, data.length * 80);
                  return (
                    <div style={{ minWidth: `${minChartWidth}px`, width: '100%', height: '100%' }} key={`chart-repair-hours-${currentSortOrder}`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={data} barCategoryGap="10%" margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip cursor={false} />
                          <ReferenceLine y={100} stroke="#ef4444" strokeDasharray="3 3" />
                          <Bar
                            dataKey="repairHours"
                            radius={[4, 4, 0, 0]}
                            barSize={34.94}
                            activeBar={false}
                            onClick={(data) => { if (data && data.name) handleEquipmentDrillDown(data.name, data.equipmentId || data.name); }}
                            style={{ cursor: 'pointer' }}
                            fill="#ec4899"
                          >
                            {data.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={entry.equipmentId === selectedEquipmentIdForDrillDown ? "#1e40af" : "#ec4899"}
                              />
                            ))}
                          </Bar>
                          <Line type="monotone" dataKey="avgRepair" stroke="#ef4444" strokeWidth={2} dot={false} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
              {/* Fixed X-axis label */}
              <div className="absolute bottom-2 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '60px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                Equipment No
              </div>
            </div>
          </div>

          {/* Chart 2: Sum of Repair Hours by Type of Service */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold text-gray-900">Sum of Repair Hours by Type of Service</h3>
              <div className="flex items-center gap-2">
                <Select
                  value={sortOrder['sum-of-repair-hours-by-service-type'] || 'low-high'}
                  onValueChange={(value) => {
                    setSortOrder(prev => ({ ...prev, 'sum-of-repair-hours-by-service-type': value }));
                  }}
                >
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="sum-of-repair-hours-by-service-type" />
              </div>
            </div>
            <div className="flex-1 p-2 flex overflow-hidden">
              <div className="flex-[1.5] min-w-0">
                {(() => {
                  let data = sumOfRepairHoursByServiceType || [];
                  const currentSortOrder = sortOrder['sum-of-repair-hours-by-service-type'] || 'low-high';
                  if (data.length > 0) {
                    data = [...data].sort((a, b) => {
                      const aValue = Number(a.value) || 0;
                      const bValue = Number(b.value) || 0;
                      return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                    });
                  }
                  return (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={data}
                          cx="50%"
                          cy="50%"
                          innerRadius={0}
                          outerRadius={45}
                          dataKey="value"
                          labelLine={{ stroke: '#9ca3af', strokeWidth: 1 }}
                          label={renderCustomLabel}
                          onClick={(data) => { if (data && data.name) handleServiceTypeDrillDown(data.name); }}
                        >
                          {data.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={entry.name === selectedServiceTypeForDrillDown ? "#1e40af" : entry.fill}
                              style={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  );
                })()}
              </div>
              <div className="flex-1 flex flex-col gap-1 ml-1 overflow-y-auto pr-1 custom-scrollbar">
                <h4 className="text-[10px] font-bold text-gray-900 mb-1">Type of Service</h4>
                {(() => {
                  let data = sumOfRepairHoursByServiceType || [];
                  const currentSortOrder = sortOrder['sum-of-repair-hours-by-service-type'] || 'low-high';
                  if (data.length > 0) {
                    data = [...data].sort((a, b) => {
                      const aValue = Number(a.value) || 0;
                      const bValue = Number(b.value) || 0;
                      return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                    });
                  }
                  return data.map((item, index) => (
                    <div
                      key={index}
                      className={`flex items-center gap-1.5 min-w-0 cursor-pointer hover:bg-gray-100 rounded px-1 transition-colors ${item.name === selectedServiceTypeForDrillDown ? 'bg-blue-50' : ''}`}
                      onClick={() => handleServiceTypeDrillDown(item.name)}
                    >
                      <span className="w-2.5 h-2.5 rounded-[3px] shrink-0" style={{ backgroundColor: item.name === selectedServiceTypeForDrillDown ? "#1e40af" : item.fill }}></span>
                      <span className={`text-[9px] truncate ${item.name === selectedServiceTypeForDrillDown ? 'text-blue-700 font-bold' : 'text-gray-500'}`} title={item.name}>{item.name}</span>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>

          {/* Chart 3: MTBF(hours) by Equipment No */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold text-gray-900">MTBF(hours) by Equipment No</h3>
              <div className="flex items-center gap-2">
                <Select
                  value={sortOrder['mtbf-by-equipment-no'] || 'low-high'}
                  onValueChange={(value) => {
                    setSortOrder(prev => ({ ...prev, 'mtbf-by-equipment-no': value }));
                  }}
                >
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="mtbf-by-equipment-no" />
              </div>
            </div>
            <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
              {/* Fixed Y-axis label */}
              <div className="absolute left-8 top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                  Equipment No
                </div>
              </div>
              {/* Scrollable chart container */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden relative chart-scroll" style={{ marginLeft: '60px', marginBottom: '30px' }}>
                {(() => {
                  let data = mtbfByEquipmentNo || [];
                  const currentSortOrder = sortOrder['mtbf-by-equipment-no'] || 'low-high';
                  if (data.length > 0) {
                    data = [...data].sort((a, b) => {
                      const aValue = Number(a.mtbf) || 0;
                      const bValue = Number(b.mtbf) || 0;
                      return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                    });
                  }
                  const minChartHeight = Math.max(200, data.length * 60);
                  return (
                    <div style={{ minHeight: `${minChartHeight}px`, width: '100%', height: '100%' }} key={`chart-mtbf-equipment-${currentSortOrder}`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} layout="vertical" barCategoryGap="40%" margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                          <XAxis type="number" tick={{ fontSize: 12 }} domain={[0, 'auto']} />
                          <YAxis type="category" dataKey="equipment" width={100} tick={{ fontSize: 11 }} tickFormatter={(value) => value && value.length > 12 ? `${value.substring(0, 12)}...` : value} />
                          <Tooltip cursor={false} />
                          <Bar
                            dataKey="mtbf"
                            radius={[0, 4, 4, 0]}
                            barSize={28}
                            activeBar={false}
                            onClick={(data) => { if (data && data.name) handleEquipmentDrillDown(data.name, data.equipmentId || data.name); }}
                            style={{ cursor: 'pointer' }}
                            fill="#a78bfa"
                          >
                            {data.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={entry.equipmentId === selectedEquipmentIdForDrillDown ? "#1e40af" : "#a78bfa"}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
              {/* Fixed X-axis label */}
              <div className="absolute bottom-0 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '60px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                MTBF (hours)
              </div>
            </div>
          </div>


          {/* Chart 4: MDT(hours) by Equipment No */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold text-gray-900">MDT(hours) by Equipment No</h3>
              <div className="flex items-center gap-2">
                <Select
                  value={sortOrder['mdt-by-equipment-no'] || 'low-high'}
                  onValueChange={(value) => {
                    setSortOrder(prev => ({ ...prev, 'mdt-by-equipment-no': value }));
                  }}
                >
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="mdt-by-equipment-no" />
              </div>
            </div>
            <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
              {/* Fixed Y-axis label */}
              <div className="absolute left-8 top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                  Equipment No
                </div>
              </div>
              {/* Scrollable chart container */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden relative chart-scroll" style={{ marginLeft: '60px', marginBottom: '30px' }}>
                {(() => {
                  let data = mdtByEquipmentNo || [];
                  const currentSortOrder = sortOrder['mdt-by-equipment-no'] || 'low-high';
                  if (data.length > 0) {
                    data = [...data].sort((a, b) => {
                      const aValue = Number(a.mdt) || 0;
                      const bValue = Number(b.mdt) || 0;
                      return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                    });
                  }
                  const minChartHeight = Math.max(200, data.length * 60);
                  return (
                    <div style={{ minHeight: `${minChartHeight}px`, width: '100%', height: '100%' }} key={`chart-mdt-equipment-${currentSortOrder}`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} layout="vertical" barCategoryGap="40%" margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                          <XAxis type="number" tick={{ fontSize: 12 }} domain={[0, 'auto']} />
                          <YAxis type="category" dataKey="equipment" width={100} tick={{ fontSize: 11 }} tickFormatter={(value) => value && value.length > 12 ? `${value.substring(0, 12)}...` : value} />
                          <Tooltip cursor={false} />
                          <Bar
                            dataKey="mdt"
                            radius={[0, 4, 4, 0]}
                            barSize={28}
                            activeBar={false}
                            onClick={(data) => { if (data && data.name) handleEquipmentDrillDown(data.name, data.equipmentId || data.name); }}
                            style={{ cursor: 'pointer' }}
                            fill="#06b6d4"
                          >
                            {data.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={entry.equipmentId === selectedEquipmentIdForDrillDown ? "#1e40af" : "#06b6d4"}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
              {/* Fixed X-axis label */}
              <div className="absolute bottom-0 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '60px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                MDT (hours)
              </div>
            </div>
          </div>

          {/* Chart 5: MDT(hours) by Type of Service */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold text-gray-900">MDT(hours) by Type of Service</h3>
              <div className="flex items-center gap-2">
                <Select
                  value={sortOrder['mdt-by-service-type'] || 'low-high'}
                  onValueChange={(value) => {
                    setSortOrder(prev => ({ ...prev, 'mdt-by-service-type': value }));
                  }}
                >
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="mdt-by-service-type" />
              </div>
            </div>
            <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
              {/* Fixed Y-axis label */}
              <div className="absolute left-8 top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                  Type of Service
                </div>
              </div>
              {/* Scrollable chart container */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden relative chart-scroll" style={{ marginLeft: '60px', marginBottom: '30px' }}>
                {(() => {
                  let data = mdtByServiceType || [];
                  const currentSortOrder = sortOrder['mdt-by-service-type'] || 'low-high';
                  if (data.length > 0) {
                    data = [...data].sort((a, b) => {
                      const aValue = Number(a.mdt) || 0;
                      const bValue = Number(b.mdt) || 0;
                      return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                    });
                  }
                  const minChartHeight = Math.max(200, data.length * 60);
                  return (
                    <div style={{ minHeight: `${minChartHeight}px`, width: '100%', height: '100%' }} key={`chart-mdt-service-${currentSortOrder}`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} layout="vertical" barCategoryGap="40%" margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                          <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(value) => value >= 1000 ? `${value / 1000}K` : value.toString()} domain={[0, 'auto']} />
                          <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} tickFormatter={(value) => value.length > 12 ? `${value.substring(0, 12)}...` : value} />
                          <Tooltip cursor={false} />
                          <Bar
                            dataKey="mdt"
                            radius={[0, 4, 4, 0]}
                            barSize={28}
                            activeBar={false}
                            fill="#10b981"
                            onClick={(data) => { if (data && data.name) handleServiceTypeDrillDown(data.name); }}
                            style={{ cursor: 'pointer' }}
                          >
                            {data.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={entry.name === selectedServiceTypeForDrillDown ? "#1e40af" : "#10b981"}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
              {/* Fixed X-axis label */}
              <div className="absolute bottom-0 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '60px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                MDT (hours)
              </div>
            </div>
          </div>

          {/* Chart 6: MTBF(hours) by Type of Service */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold text-gray-900">MTBF(hours) by Type of Service</h3>
              <div className="flex items-center gap-2">
                <Select
                  value={sortOrder['mtbf-by-service-type'] || 'low-high'}
                  onValueChange={(value) => {
                    setSortOrder(prev => ({ ...prev, 'mtbf-by-service-type': value }));
                  }}
                >
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="mtbf-by-service-type" />
              </div>
            </div>
            <div className="flex-1 p-6 pt-4 flex flex-col overflow-hidden relative">
              {/* Fixed Y-axis label */}
              <div className="absolute left-8 top-0 bottom-14 z-30 pointer-events-none flex items-center" style={{ width: '60px', paddingLeft: '15px' }}>
                <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)', fontSize: '11px', fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>
                  Type of Service
                </div>
              </div>
              {/* Scrollable chart container */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden relative chart-scroll" style={{ marginLeft: '60px', marginBottom: '30px' }}>
                {(() => {
                  let data = mtbfByServiceType || [];
                  const currentSortOrder = sortOrder['mtbf-by-service-type'] || 'low-high';
                  if (data.length > 0) {
                    data = [...data].sort((a, b) => {
                      const aValue = Number(a.mtbf) || 0;
                      const bValue = Number(b.mtbf) || 0;
                      return currentSortOrder === 'low-high' ? aValue - bValue : bValue - aValue;
                    });
                  }
                  const minChartHeight = Math.max(200, data.length * 60);
                  return (
                    <div style={{ minHeight: `${minChartHeight}px`, width: '100%', height: '100%' }} key={`chart-mtbf-service-${currentSortOrder}`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} layout="vertical" barCategoryGap="40%" margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                          <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(value) => value >= 1000 ? `${value / 1000}K` : value.toString()} domain={[0, 'auto']} />
                          <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} tickFormatter={(value) => value.length > 12 ? `${value.substring(0, 12)}...` : value} />
                          <Tooltip cursor={false} />
                          <Bar
                            dataKey="mtbf"
                            radius={[0, 4, 4, 0]}
                            barSize={28}
                            activeBar={false}
                            fill="#f97316"
                            onClick={(data) => { if (data && data.name) handleServiceTypeDrillDown(data.name); }}
                            style={{ cursor: 'pointer' }}
                          >
                            {data.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={entry.name === selectedServiceTypeForDrillDown ? "#1e40af" : "#f97316"}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  );
                })()}
              </div>
              {/* Fixed X-axis label */}
              <div className="absolute bottom-0 left-0 right-0 z-30 pointer-events-none flex justify-center items-center" style={{ height: '30px', marginLeft: '60px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>
                MTBF (hours)
              </div>
            </div>
          </div>


          {/* Chart 7: Sum of Uptime by Year, Quarter, Month and Day */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold text-gray-900">Sum of Uptime by Year, Quarter, Month and Day</h3>
              <div className="flex items-center gap-2">
                <Select value={sumOfUptimeResolution} onValueChange={setSumOfUptimeResolution}>
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue placeholder="Res" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Day</SelectItem>
                    <SelectItem value="month">Month</SelectItem>
                    <SelectItem value="quarter">Quarter</SelectItem>
                    <SelectItem value="year">Year</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="sum-of-uptime-by-date" />
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center p-4 pb-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={sumOfUptimeByDate} margin={{ top: 5, right: 20, left: 10, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} label={{ value: 'Date', position: 'insideBottom', offset: -1, style: { textAnchor: 'middle', fontSize: 12 } }} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `${value}bn`}
                    domain={[0, 2]}
                    label={{ value: 'Sum of Uptime', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12 } }}
                  />
                  <Tooltip />
                  <Line type="monotone" dataKey="current" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="forecast" stroke="#f97316" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4 }} />
                  <ReferenceLine y={1.7} stroke="#000" strokeDasharray="3 3" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-6 px-6 pb-2">
              <div className="flex items-center text-xs text-gray-600">
                <span className="w-3 h-3 rounded bg-[#3b82f6] mr-2 inline-block"></span>
                Current
              </div>
              <div className="flex items-center text-xs text-gray-600">
                <span className="w-3 h-3 rounded bg-[#f97316] mr-2 inline-block"></span>
                Forecast
              </div>
            </div>
          </div>

          {/* Chart 8: MDT(Hours) by Date */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold text-gray-900">MDT(Hours) by Date</h3>
              <div className="flex items-center gap-2">
                <Select
                  value={sortOrder['mdt-by-date'] || 'low-high'}
                  onValueChange={(value) => setSortOrder(prev => ({ ...prev, 'mdt-by-date': value }))}
                >
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low-high">Low to High</SelectItem>
                    <SelectItem value="high-low">High to Low</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="mdt-by-date" />
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center p-4 pb-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={(() => {
                    let data = mdtByDate || [];
                    const currentSort = sortOrder['mdt-by-date'] || 'low-high';
                    if (data.length > 0) {
                      return [...data].sort((a, b) => {
                        const aVal = Number(a.current) || 0;
                        const bVal = Number(b.current) || 0;
                        return currentSort === 'low-high' ? aVal - bVal : bVal - aVal;
                      });
                    }
                    return data;
                  })()}
                  margin={{ top: 5, right: 20, left: 10, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} label={{ value: 'Date', position: 'insideBottom', offset: -1, style: { textAnchor: 'middle', fontSize: 12 } }} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    domain={[0, 5]}
                    label={{ value: 'MDT (hours)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12 } }}
                  />
                  <Tooltip />
                  <Line type="monotone" dataKey="current" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="forecast" stroke="#f97316" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4 }} />
                  <ReferenceLine y={4.0} stroke="#000" strokeDasharray="3 3" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-6 px-6 pb-2">
              <div className="flex items-center text-xs text-gray-600">
                <span className="w-3 h-3 rounded bg-[#3b82f6] mr-2 inline-block"></span>
                Current
              </div>
              <div className="flex items-center text-xs text-gray-600">
                <span className="w-3 h-3 rounded bg-[#f97316] mr-2 inline-block"></span>
                Forecast
              </div>
            </div>
          </div>

          {/* Chart 9: MTBF (Hours) by Year, Quarter, Month and Day */}
          <div className="bg-white h-[160px] rounded-lg shadow-sm p-0 border border-gray-200 flex flex-col">
            <div className="bg-gray-50 flex items-center justify-between px-4 py-2 rounded-t-lg">
              <h3 className="text-sm font-bold text-gray-900">MTBF (Hours) by Year, Quarter, Month and Day</h3>
              <div className="flex items-center gap-2">
                <Select value={mtbfResolution} onValueChange={setMtbfResolution}>
                  <SelectTrigger className="w-[85px] h-6 text-[10px] bg-white">
                    <SelectValue placeholder="Res" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Day</SelectItem>
                    <SelectItem value="month">Month</SelectItem>
                    <SelectItem value="quarter">Quarter</SelectItem>
                    <SelectItem value="year">Year</SelectItem>
                  </SelectContent>
                </Select>
                <MaximizeButton chartId="mtbf-by-date" />
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center p-4 pb-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={mtbfByDate} margin={{ top: 5, right: 20, left: 10, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} label={{ value: 'Date', position: 'insideBottom', offset: -1, style: { textAnchor: 'middle', fontSize: 12 } }} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `${value}K`}
                    domain={[0, 10]}
                    label={{ value: 'MTBF (hours)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 12 } }}
                  />
                  <Tooltip />
                  <Line type="monotone" dataKey="current" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="forecast" stroke="#f97316" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4 }} />
                  <ReferenceLine y={5.5} stroke="#000" strokeDasharray="3 3" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-6 px-6 pb-2">
              <div className="flex items-center text-xs text-gray-600">
                <span className="w-3 h-3 rounded bg-[#3b82f6] mr-2 inline-block"></span>
                Current
              </div>
              <div className="flex items-center text-xs text-gray-600">
                <span className="w-3 h-3 rounded bg-[#f97316] mr-2 inline-block"></span>
                Forecast
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chart Modal for Maximized View */}
      {maximizedChartId && (
        <ChartModal
          isOpen={!!maximizedChartId}
          onClose={handleCloseModal}
          title={chartConfigs[maximizedChartId]?.title || 'Chart'}
          headerActions={renderHeaderActions(maximizedChartId)}
          showEquipmentFilter={!['sum-of-uptime-by-date', 'mdt-by-date', 'mtbf-by-date'].includes(maximizedChartId)}
          equipmentList={getEquipmentListForChart(maximizedChartId)}
          selectedEquipment={modalEquipmentFilter}
          onEquipmentChange={setModalEquipmentFilter}
          filterLabel={getFilterLabelForChart(maximizedChartId)}
          showSortFilter={chartConfigs[maximizedChartId]?.chartType === 'pie'}
          sortOrder={modalSortOrder}
          onSortChange={setModalSortOrder}
        >
          {renderMaximizedChart(maximizedChartId)}
        </ChartModal>
      )}
    </div>
  );
};

export default Maintenance;
